import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useOwnerLayoutData } from '@/hooks/useOwnerLayoutData';
import { APP_CONFIG } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ApperIcon from '@/components/ApperIcon';
import UserMenu from '@/components/UserMenu';
import BrandLogo from '@/components/BrandLogo';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { Button } from '@/components/ui/button';
import { sdk } from '@/services/sdk';
import { useProfile } from '@/hooks/useProfile';
import { isTeamProfile } from '@/services/userPermissions';
import { INSTALL_PATH } from '@/shared/pwa';
import { syncAllHandles } from '@/shared/referrals';
import LanguagePicker from '@/components/LanguagePicker';
import { useT } from '@/shared/i18n';

function NavList({ groups, counts, onNavigate }) {
  const { t } = useT();
  return (
    <div className="space-y-6">
      {groups.map((g, i) => (
        <div key={g.label ?? `g-${i}`} className="space-y-1">
          {g.label && (
            <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.24em] text-sidebar-foreground/50">{t(g.label)}</p>
          )}
          {g.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin' || item.to === '/account'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-(--transition-fast)',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <ApperIcon
                    name={item.icon ?? 'Circle'}
                    size={17}
                    className={cn(isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/60')}
                  />
                  <span className="min-w-0 flex-1 truncate">{t(item.label)}</span>
                  {counts?.[item.to] != null && (
                    <span className="rounded-sm bg-sidebar-primary/15 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-sidebar-primary">
                      {counts[item.to]}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </div>
  );
}

function Brand({ to }) {
  return (
    <Link to={to} className="flex items-center gap-2.5">
      <BrandLogo size={32} />
      <span className="font-heading text-2xl leading-none">{APP_CONFIG.name}</span>
    </Link>
  );
}

export default function OwnerLayout() {
  const { user, logout, homeRoute, navItems, navGroups, initials, displayName, counts } = useOwnerLayoutData();
  const groups = navGroups?.length ? navGroups : [{ label: null, items: navItems ?? [] }];
  const [navOpen, setNavOpen] = useState(false);
  const { profile } = useProfile();
  const { t } = useT();
  const customer = !isTeamProfile(profile);
  const ratesChecked = useRef(false);
  const handlesSynced = useRef(false);

  // Readable back-office type (see .owner-theme in theme.css). On body so portals inherit it.
  useEffect(() => {
    document.body.classList.add('owner-theme');
    return () => document.body.classList.remove('owner-theme');
  }, []);

  // Keep currency rates fresh: the edge function only refetches when the stored rates are a day old.
  useEffect(() => {
    if (ratesChecked.current || !isTeamProfile(profile) || !import.meta.env.VITE_REFRESH_RATES) return;
    ratesChecked.current = true;
    (async () => {
      try {
        await sdk.functions.invoke(import.meta.env.VITE_REFRESH_RATES, {});
      } catch {
        // Rates stay as they were; the Currencies page has a manual refresh.
      }
    })();
  }, [profile]);

  // Once a day, republish the usernames of active, paid lines so buyers can use them as referrals.
  useEffect(() => {
    if (handlesSynced.current || !isTeamProfile(profile)) return;
    handlesSynced.current = true;
    const key = 'gc:referral-usernames-synced';
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (window.localStorage.getItem(key) === today) return;
    } catch {
      // no storage: sync anyway
    }
    syncAllHandles()
      .then(() => {
        try {
          window.localStorage.setItem(key, today);
        } catch {
          // fine
        }
      })
      .catch(() => {});
  }, [profile]);

  return (
    <div className="min-h-svh bg-background text-foreground md:grid md:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:sticky md:top-0 md:flex md:h-svh md:flex-col">
        <div className="flex h-(--spacing-header) items-center border-b border-sidebar-border px-5">
          <Brand to={homeRoute} />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5" aria-label="Main">
          <NavList groups={groups} counts={counts} />
        </nav>
        <div className="border-t border-sidebar-border px-5 py-4">
          <p className="font-heading text-lg leading-tight text-sidebar-foreground/80">{APP_CONFIG.tagline}</p>
          <Link
            to="/"
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-sidebar-foreground/60 hover:text-sidebar-primary"
          >
            <ApperIcon name="ArrowUpRight" size={14} />
            {t('Visit the storefront')}
          </Link>
          <Link
            to={INSTALL_PATH}
            className="mt-1.5 flex items-center gap-1.5 text-xs text-sidebar-foreground/60 hover:text-sidebar-primary"
          >
            <ApperIcon name="Smartphone" size={14} />
            {t('Install the app')}
          </Link>
        </div>
      </aside>

      <div className="flex min-h-svh min-w-0 flex-col">
        <header className="sticky top-0 z-(--z-sticky) flex h-(--spacing-header) items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur md:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            <ApperIcon name={navOpen ? 'X' : 'Menu'} size={20} />
          </Button>
          <Link to={homeRoute} className="flex items-center gap-2 md:hidden">
            <BrandLogo size={26} />
            <span className="font-heading text-xl leading-none">{APP_CONFIG.name}</span>
          </Link>
          <p className="hidden text-xs uppercase tracking-[0.22em] text-muted-foreground md:block">
            {t('Broadcasting from beyond')}
          </p>
          <div className="ms-auto flex items-center gap-2">
            {customer && <LanguagePicker />}
            <UserMenu user={user} initials={initials} displayName={displayName} logout={logout} />
          </div>
        </header>

        {navOpen && (
          <nav className="border-b border-sidebar-border bg-sidebar px-3 py-4 text-sidebar-foreground md:hidden" aria-label="Main">
            <NavList groups={groups} counts={counts} onNavigate={() => setNavOpen(false)} />
          </nav>
        )}

        <main className="flex-1 px-4 py-6 md:px-8 md:py-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
